import React, { useState, useEffect, useRef } from 'react';
import styles from './Contact.module.css';
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { apiClient } from '../../services';
import { CreateQuoteRequestRequestDTO } from '@onaptek/lbtiling.restapi-access-sdk';
import SuccessModal from '../components/SuccessModal';
import Button from '../components/Button';
import SocialIcons from '../components/SocialIcons';

const Contact: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState<CreateQuoteRequestRequestDTO | null>(null);
  const [formData, setFormData] = useState<CreateQuoteRequestRequestDTO>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [visibleElements, setVisibleElements] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Staggered animation for title, subtitle, note, form fields (4), button, contact info, social icons
            const totalElements = 10;
            for (let i = 0; i < totalElements; i++) {
              setTimeout(() => {
                setVisibleElements(prev => {
                  if (!prev.includes(i)) {
                    return [...prev, i];
                  }
                  return prev;
                });
              }, i * 150); // 150ms delay between each element
            }
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous error messages when user starts typing
    if (submitError) setSubmitError(null);
  };

  const validateQuoteRequestData = (data: CreateQuoteRequestRequestDTO): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Name is required
    if (!data.name || data.name.trim() === '') {
      errors.push('Name is required');
    }

    // Either email or phone must be provided
    const hasEmail = data.email && data.email.trim() !== '';
    const hasPhone = data.phone && data.phone.trim() !== '';
    
    if (!hasEmail && !hasPhone) {
      errors.push('Either email or phone number is required');
    }

    // Validate email format if provided
    if (hasEmail && data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.push('Invalid email format');
      }
    }

    // Validate phone format if provided (North American format)
    if (hasPhone && data.phone) {
      const phoneDigits = data.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        errors.push('Invalid phone number format (should be 10-11 digits)');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Validate form data
    const validation = validateQuoteRequestData(formData);
    if (!validation.isValid) {
      setSubmitError(validation.errors.join(', '));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await apiClient.quoteRequests.createQuoteRequest(formData);
      
      if (response.isSuccessful) {
        // Store submitted data for modal display
        setSubmittedData({ ...formData });
        setShowSuccessModal(true);
        // Form will be reset when modal is closed
      } else {
        setSubmitError(response.errors[0].message || 'Failed to submit quote request');
      }
    } catch (error: any) {
      console.error('Error submitting quote request:', error);
      if (error?.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className={`${styles.contactSection} trowel-background`}>
      <h1 className={`${styles.title} ${visibleElements.includes(0) ? styles.fadeInDown : styles.animationHidden}`}>
        Contact Us / Request a Quote
      </h1>
      <p className={`${styles.subtitle} ${visibleElements.includes(1) ? styles.fadeInUp : styles.animationHidden}`}>
        Please fill out the form below and we'll get back to you shortly.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={`${styles.formGroup} ${visibleElements.includes(3) ? styles.slideInLeft : styles.animationHidden}`}>
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={`${styles.formGroup} ${visibleElements.includes(4) ? styles.slideInRight : styles.animationHidden}`}>
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email || ''}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div className={`${styles.formGroup} ${visibleElements.includes(5) ? styles.slideInLeft : styles.animationHidden}`}>
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handleInputChange}
            placeholder="(250) 555-1234"
            required
          />
        </div>

        <div className={`${styles.formGroup} ${visibleElements.includes(6) ? styles.slideInRight : styles.animationHidden}`}>
          <label htmlFor="message">Message:</label>
          <textarea
            id="message"
            name="message"
            value={formData.message || ''}
            onChange={handleInputChange}
            required
          />
        </div>

        {submitError && (
          <div className={styles.errorMessage}>
            {submitError}
          </div>
        )}

        <div className={visibleElements.includes(7) ? styles.fadeInUp : styles.animationHidden}>
          <Button
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Send
          </Button>
        </div>
      </form>

      <div className={`${styles.contactInfo} ${visibleElements.includes(8) ? styles.fadeInUp : styles.animationHidden}`}>
        <div className={styles.infoItem}><FaMapMarkerAlt />Revelstoke, BC</div>
        <div className={styles.infoItem}><FaPhoneAlt /> <a href="tel:+12505551234">(250) 814-7415</a></div>
        <div className={styles.infoItem}><FaEnvelope /> <a href="mailto:info@lbtiling.ca">info@lbtiling.ca</a></div>
      </div>

      <div className={`${styles.contactSocialIcons} ${visibleElements.includes(9) ? styles.fadeInUp : styles.animationHidden}`}>
        <SocialIcons hoverColor="#e8e9eb" />
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          // Clear form when modal is closed
          setFormData({ name: '', email: '', phone: '', message: '' });
        }}
        title="Quote Request Submitted!"
        message="Thank you for your interest! We have received your information and will get back to you shortly."
        submittedData={submittedData || {}}
      />
    </section>
  );
};

export default Contact;